/**
 * PermissionGuard Component
 * Shows/hides UI elements based on user permissions
 * 
 * Usage Examples:
 * 
 * 1. Single permission:
 *    <PermissionGuard permission="view_orders">
 *      <OrdersTab />
 *    </PermissionGuard>
 * 
 * 2. Multiple permissions (any):
 *    <PermissionGuard permissions={['view_orders', 'view_payments']}>
 *      <FinanceDashboard />
 *    </PermissionGuard>
 * 
 * 3. Multiple permissions (all required):
 *    <PermissionGuard permissions={['view_orders', 'edit_orders']} requireAll>
 *      <EditOrderButton />
 *    </PermissionGuard>
 * 
 * 4. With fallback:
 *    <PermissionGuard permission="export_orders" fallback={<div>No access</div>}>
 *      <ExportButton />
 *    </PermissionGuard>
 */

import React, { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';

interface PermissionGuardProps {
  permission?: string;       // Single permission code
  permissions?: string[];    // Array of permission codes
  requireAll?: boolean;      // If true, requires ALL permissions. If false, requires ANY
  children: ReactNode;       // Content to show if user has permission
  fallback?: ReactNode;      // Content to show if user doesn't have permission (optional)
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  permission,
  permissions,
  requireAll = false,
  children,
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  // Check single permission
  if (permission) {
    hasAccess = hasPermission(permission);
  }
  // Check multiple permissions
  else if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasAccess = hasAllPermissions(permissions);
    } else {
      hasAccess = hasAnyPermission(permissions);
    }
  }
  // No permission specified, allow access
  else {
    hasAccess = true;
  }

  // Show children if has access, otherwise show fallback (or null)
  return <>{hasAccess ? children : fallback}</>;
};

